import React from 'react';

export const BlogLiferayEditors = (props) => {
    return (
        <div className='blog-con'>
            <hr />
            <article>
                <p>Liferay DXP offers extensive flexibility for customizing and configuring its rich text editors. If you need to modify the configuration of an editor, such as adding new buttons or changing behavior, you can do so by implementing the <code>EditorConfigContributor</code> interface. This guide walks you through the process of creating an OSGi module that will allow you to modify editor configurations for specific portlets or globally within Liferay.</p>

                <h2>Step-by-Step Guide for Configurations</h2>
                <br />
                <h3>1. Create an OSGi Module</h3>
                <p>The first step in modifying an editor's configuration is creating a new OSGi module within your Liferay project. This module will contain the logic for configuring the editor.</p>
                <ul>
                    <li><strong>Create the Module</strong>: In your Liferay project, create an OSGi module using Liferay’s module creation tools or IDE integration (e.g., Liferay Workspace, Blade CLI, or Eclipse with Liferay tools).</li>
                    <li><strong>Define the Package and Class</strong>: Inside the module's <code>src</code> directory, create a unique package name (e.g., <code>com.example.editorconfig</code>) and add a new Java class within that package. This class will extend the <code>BaseEditorConfigContributor</code> class, which provides a foundation for contributing to editor configurations.</li>
                </ul>

                <h3>2. Implement the <code>EditorConfigContributor</code> Service</h3>
                <p>To make the editor configuration changes, your class must implement the <code>EditorConfigContributor</code> service. This service is used to modify or extend editor configurations for specific portlets or globally.</p>

                <pre><code>
{`@Component(
    property = {
        "editor.name=ckeditor", // For ckeditors configuration
        // "editor.name=alloyeditor",  Use this For alloyeditor's configuration
        "javax.portlet.name=com_liferay_blogs_web_portlet_BlogsPortlet",
        "javax.portlet.name=com_liferay_blogs_web_portlet_BlogsAdminPortlet",
        "service.ranking:Integer=100"
    },
    service = EditorConfigContributor.class
)
public class MyEditorConfigContributor extends BaseEditorConfigContributor {
    @Override
    public void populateConfigJSONObject(
        JSONObject jsonObject, Map<String, Object> inputEditorTaglibAttributes,
        ThemeDisplay themeDisplay,
        RequestBackedPortletURLFactory requestBackedPortletURLFactory) {

        // Logic to modify the editor's configuration goes here

    }
}`}
                </code></pre>

                <h3>3. Understanding the <code>@Component</code> Annotation</h3>
                <p>The <code>@Component</code> annotation is crucial as it registers your service and specifies the editor configuration properties. In this example, we are targeting the <strong>CKEditor</strong> used within the <strong>Blogs</strong> and <strong>Blogs Admin</strong> portlets:</p>
                <ul>
                    <li><strong>editor.name</strong>: Indicates the editor's name (e.g., <code>ckeditor</code>).</li>
                    <li><strong>javax.portlet.name</strong>: Specifies the portlet where the editor resides (e.g., <code>BlogsPortlet</code> and <code>BlogsAdminPortlet</code>).</li>
                    <li><strong>service.ranking</strong>: Defines the priority of your service relative to other services. The higher the number, the higher the priority.</li>
                </ul>

                <h3>4. Modify Editor Configuration in <code>populateConfigJSONObject</code></h3>
                <p>The <code>populateConfigJSONObject</code> method is where you modify the actual editor configuration. The method receives a <code>JSONObject</code> representing the current editor configuration, and you can update this object to reflect the changes you want.</p>

                <pre><code>
{`@Override
public void populateConfigJSONObject(
    JSONObject jsonObject, Map<String, Object> inputEditorTaglibAttributes,
    ThemeDisplay themeDisplay,
    RequestBackedPortletURLFactory requestBackedPortletURLFactory) {

    JSONArray newStylesSetArray = JSONFactoryUtil.createJSONArray();
    
    //Adding Superscript and Subscript to CK editor
    JSONArray newJsonArray = JSONFactoryUtil.createJSONArray();
    newJsonArray.put("Superscript");
    newJsonArray.put("Subscript");

    jsonObject.getJSONArray("toolbar_liferay").put(newJsonArray);

}`}
                </code></pre>

                <p>In this code:</p>
                <ul>
                    <li><strong>jsonObject</strong>: The editor's current configuration.</li>
                    <li><strong>toolbars</strong>: This retrieves the toolbar configuration.</li>
                    <li><strong>Superscript</strong>: A superscript is a character, number, or symbol that is positioned slightly above the baseline of the text.</li>
                    <li><strong>Subscript</strong>: A subscript is a character, number, or symbol that is positioned slightly below the baseline of the text.</li>
                </ul>
                <img src="/images/blogs/LiferayEditor2.png" alt="wysiwyg-editor" style={{width: '100%'}} />
                <h3>5. Handle Multiple Portlets and Editors</h3>
                <p>You may want to apply this configuration globally across all instances of an editor, or only within specific portlets. You can target multiple portlets by specifying their names in the <code>@Component</code> annotation. If you want to apply your configuration across all portlets where the editor is used, you can omit the <code>javax.portlet.name</code> properties.</p>

                <pre><code>
{`@Component(
    property = {
        "editor.name=ckeditor",
        "service.ranking:Integer=100"
    },
    service = EditorConfigContributor.class
)
public class GlobalEditorConfigContributor extends BaseEditorConfigContributor {
    @Override
    public void populateConfigJSONObject(
        JSONObject jsonObject, Map<String, Object> inputEditorTaglibAttributes,
        ThemeDisplay themeDisplay,
        RequestBackedPortletURLFactory requestBackedPortletURLFactory) {  
        
        // Modify global editor configuration here

    }
}`}
                </code></pre>

                <h3>6. Deploy the Module</h3>
                <p>Once you have completed the configuration, the final step is to generate the module’s JAR file and deploy it to your Liferay portal:</p>
                <ul>
                    <li><strong>Build the Module</strong>: Use your build tools (such as Maven or Gradle) to build the module into a JAR file.</li>
                    <li><strong>Deploy</strong>: Copy the generated JAR file to your Liferay Portal's <code>deploy</code> folder.</li>
                </ul>
                <p>When the module is installed and activated, the editor configuration changes will be applied.</p>

                <h2>Conclusion</h2>
                <p>Modifying the editor configuration in Liferay DXP is a powerful way to customize the user experience within your portal. By implementing the <code>EditorConfigContributor</code> service and using the <code>@Component</code> annotation, you can target specific editors, portlets, or apply global changes. With these steps, you can easily adjust toolbar buttons, editor behavior, and much more to fit your needs.</p>

                <p>Happy coding! &#128521;</p>
            </article>
        </div>
    );
};
